<script lang="ts">
  import { tick } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import type { Task, TaskPriority } from '../lib/types';
  import { useBodyScrollLock } from '../lib/useBodyScrollLock.svelte';
  import NowToggle from './NowToggle.svelte';
  import Icon from './Icon.svelte';
  import TagPicker from './TagPicker.svelte';

  interface Props {
    task: Task | null;
    projectTitle?: string;
    projectUrl?: string;
    onClose: () => void;
    onUpdate: (task: Task, updates: Partial<Task>) => void;
    onDelete: (task: Task, pendingUpdates?: Partial<Task>) => void;
    onMarkWontDo: (task: Task, pendingUpdates?: Partial<Task>) => void;
    onToggleComplete: (task: Task, pendingUpdates?: Partial<Task>) => void;
    onProjectNavigate?: (url: string) => void;
  }

  let {
    task,
    projectTitle,
    projectUrl,
    onClose,
    onUpdate,
    onDelete,
    onMarkWontDo,
    onToggleComplete,
    onProjectNavigate,
  }: Props = $props();

  let editingTitle = $state('');
  let editingNotes = $state('');
  let editingTags = $state<string[]>([]);
  let titleInputEl: HTMLTextAreaElement | undefined = $state();
  let notesInputEl: HTMLTextAreaElement | undefined = $state();
  let previousTask = $state<Task | null>(null);
  let notesHeight = $state('auto');
  let lastOpenedTaskId: string | null = null;

  useBodyScrollLock(() => !!task);

  // eslint-disable-next-line momentium/no-unguarded-effect -- lifecycle: initialize modal edit fields on task change and auto-save on close transition
  $effect(() => {
    if (task) {
      if (lastOpenedTaskId !== task.id) {
        lastOpenedTaskId = task.id;
        editingTitle = task.title;
        editingNotes = task.notes || '';
        editingTags = (task.tags ?? []).map((t) => t.name);
      }
      previousTask = task;
    } else if (previousTask) {
      const updates = getPendingUpdatesFor(previousTask);
      if (updates) onUpdate(previousTask, updates);
      previousTask = null;
      lastOpenedTaskId = null;
    }
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM focus/measurement after modal opens
  $effect(() => {
    if (task && titleInputEl) {
      tick().then(() => {
        titleInputEl?.focus();
        autoResizeTextarea(titleInputEl);
        autoResizeTextarea(notesInputEl);
        titleInputEl?.setSelectionRange(0, 0);
      });
    }
  });

  function autoResizeTextarea(el: HTMLTextAreaElement | undefined) {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    const maxHeight = parseFloat(getComputedStyle(el).maxHeight);
    el.style.overflow = !isNaN(maxHeight) && el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    if (el === notesInputEl) notesHeight = `${el.scrollHeight}px`;
  }

  function handleTitleInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    editingTitle = target.value;
    autoResizeTextarea(target);
  }

  function handleNotesInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    editingNotes = target.value;
    autoResizeTextarea(target);
  }

  function getPendingUpdatesFor(t: Task): Partial<Task> | undefined {
    const updates: Partial<Task> = {};
    const newTitle = editingTitle.trim();
    const newNotes = editingNotes.trim() || null;
    const currentTagNames = (t.tags ?? []).map((tag) => tag.name);
    if (newTitle && newTitle !== t.title) updates.title = newTitle;
    if (newNotes !== (t.notes || null)) updates.notes = newNotes;
    if (editingTags.join('\u0000') !== currentTagNames.join('\u0000')) {
      updates.tag_names = editingTags;
    }
    return Object.keys(updates).length > 0 ? updates : undefined;
  }

  function getPendingUpdates(): Partial<Task> | undefined {
    return task ? getPendingUpdatesFor(task) : undefined;
  }

  function saveAndClose() {
    if (task) {
      const pending = getPendingUpdates();
      if (pending) onUpdate(task, pending);
      previousTask = null;
    }
    onClose();
  }

  function handleSetPriority(priority: TaskPriority) {
    if (task && task.priority !== priority) onUpdate(task, { priority });
  }

  function handleDelete() {
    if (!task) return;
    const pending = getPendingUpdates();
    previousTask = null;
    onDelete(task, pending);
    onClose();
  }

  function handleMarkWontDo() {
    if (!task) return;
    const pending = getPendingUpdates();
    previousTask = null;
    onMarkWontDo(task, pending);
    onClose();
  }

  function handleToggleComplete() {
    if (!task) return;
    const pending = getPendingUpdates();
    previousTask = null;
    onToggleComplete(task, pending);
    onClose();
  }

  function handleProjectLink() {
    const url = projectUrl;
    if (!url) return;
    saveAndClose();
    if (onProjectNavigate) {
      onProjectNavigate(url);
    } else {
      router.get(url);
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      saveAndClose();
    }
  }

  const priorityOptions: { value: TaskPriority; icon: string; label: string }[] = [
    { value: 'none', icon: '—', label: 'None' },
    { value: 'low', icon: '🔵', label: 'Low' },
    { value: 'medium', icon: '🟠', label: 'Medium' },
    { value: 'high', icon: '🔴', label: 'High' },
  ];
</script>

<svelte:window onkeydown={task ? handleKeydown : undefined} />

{#if task}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="task-modal-backdrop" onmousedown={saveAndClose} role="presentation">
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="task-modal"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      aria-labelledby="task-modal-title"
      onmousedown={(e) => e.stopPropagation()}
    >
      <div class="modal-header">
        <div>
          <span class="modal-label">Task Details</span>
          {#if projectTitle}
            <div class="project-context">
              <span>In project</span>
              {#if projectUrl}
                <button type="button" class="project-link" onclick={handleProjectLink}>
                  {projectTitle}
                </button>
              {:else}
                <span class="project-name">{projectTitle}</span>
              {/if}
            </div>
          {/if}
        </div>
        <button type="button" class="close-btn" onclick={saveAndClose} title="Close (Esc)">✕</button
        >
      </div>

      <div class="modal-body">
        <div class="field-group">
          <textarea
            id="task-modal-title"
            class="title-input"
            aria-label="Task title"
            bind:this={titleInputEl}
            bind:value={editingTitle}
            placeholder="Type your task..."
            rows="1"
            oninput={handleTitleInput}
            onkeydown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveAndClose();
              }
            }}></textarea>
        </div>

        <div class="field-group">
          <textarea
            class="notes-input"
            aria-label="Notes"
            bind:this={notesInputEl}
            bind:value={editingNotes}
            placeholder="Add notes..."
            rows="4"
            oninput={handleNotesInput}
            style={`height: ${notesHeight};`}></textarea>
        </div>

        <div class="field-group">
          <span class="field-label">Tags</span>
          <TagPicker selected={editingTags} onChange={(names) => (editingTags = names)} />
        </div>

        <div class="field-group">
          <span class="field-label">Priority</span>
          <div class="priority-picker" role="radiogroup" aria-label="Priority">
            {#each priorityOptions as opt}
              <button
                type="button"
                class="priority-option"
                class:active={task.priority === opt.value}
                onclick={() => handleSetPriority(opt.value)}
                title={opt.label}
              >
                <span class="priority-icon">{opt.icon}</span>
                <span class="priority-label">{opt.label}</span>
              </button>
            {/each}
          </div>
        </div>

        <div class="meta-info">
          <span class="meta-item">Created {new Date(task.createdAt).toLocaleDateString()}</span>
          {#if task.completedAt}
            <span class="meta-item"
              >Completed {new Date(task.completedAt).toLocaleDateString()}</span
            >
          {/if}
        </div>
      </div>

      <div class="modal-actions">
        {#if task.status === 'todo'}
          <button type="button" class="action-btn action-complete" onclick={handleToggleComplete}>
            <Icon name="check" size={12} strokeWidth={2} />Mark Done
          </button>
          <button type="button" class="action-btn action-wontdo" onclick={handleMarkWontDo}>
            <Icon name="wont-do" size={12} />Won't Do
          </button>
          <NowToggle {task} variant="action" />
        {:else}
          <button type="button" class="action-btn action-reopen" onclick={handleToggleComplete}>
            <Icon name="reopen" size={12} />Reopen
          </button>
        {/if}
        <button type="button" class="action-btn action-delete" onclick={handleDelete}>
          <Icon name="trash" size={12} />Delete
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .task-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 300;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(17, 24, 39, 0.42);
  }

  .task-modal {
    width: min(540px, 100%);
    max-height: min(720px, calc(100vh - 2rem));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.24),
      0 8px 18px rgba(0, 0, 0, 0.12);
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .modal-label {
    display: block;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .project-context {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin-top: 0.25rem;
    font-size: 0.8125rem;
    color: #6b7280;
  }

  .project-link {
    border: none;
    padding: 0;
    background: none;
    color: #2563eb;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .project-link:hover {
    text-decoration: underline;
  }

  .project-name {
    font-weight: 600;
    color: #374151;
  }

  .close-btn {
    width: 1.75rem;
    height: 1.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: #6b7280;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .close-btn:hover {
    color: #111827;
    background: #f3f4f6;
  }

  .modal-body {
    overflow-y: auto;
    padding: 0.75rem 1rem 0.25rem;
  }

  .field-group {
    margin-bottom: 0.75rem;
  }

  .field-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .title-input,
  .notes-input {
    width: 100%;
    border: none;
    border-radius: 0.375rem;
    outline: none;
    resize: none;
    background: transparent;
    transition: background-color 0.15s;
  }

  .title-input:hover,
  .notes-input:hover {
    background: rgba(0, 0, 0, 0.025);
  }

  .title-input:focus,
  .notes-input:focus {
    background: rgba(59, 130, 246, 0.05);
  }

  .title-input {
    min-height: 2.25rem;
    max-height: 9rem;
    padding: 0.375rem 0.5rem;
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.4;
    color: #111827;
  }

  .notes-input {
    min-height: 7rem;
    max-height: 18rem;
    padding: 0.5rem;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #4b5563;
  }

  .priority-picker {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .priority-option {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.55rem;
    font-size: 0.8125rem;
    color: #4b5563;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .priority-option:hover {
    background: #f3f4f6;
  }

  .priority-option.active {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .meta-info {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding: 0.25rem 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    background: #fafafa;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3125rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 600;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    cursor: pointer;
    background: white;
  }

  .action-complete,
  .action-reopen {
    color: #047857;
    border-color: #a7f3d0;
    background: #ecfdf5;
  }

  .action-wontdo {
    color: #92400e;
    border-color: #fde68a;
    background: #fffbeb;
  }

  .action-delete {
    color: #dc2626;
    border-color: #fecaca;
    background: #fef2f2;
  }

  @media (max-width: 640px) {
    .task-modal-backdrop {
      align-items: flex-end;
      padding: 0;
    }

    .task-modal {
      width: 100%;
      max-height: calc(100vh - 1rem);
      border-radius: 0.875rem 0.875rem 0 0;
    }
  }
</style>
